<template>
	<DialogControl :attach="overlayTarget" v-model="open" fullscreen scrollable class="competitor-positioning-dialog">
		<v-card class="competitor-positioning-dialog__card">
			<v-card-title class="headline">
				<v-icon>leaderboard</v-icon>
				{{ t('CompetitorPositioning') }}
				<v-spacer></v-spacer>
				<v-btn icon="close" variant="tonal" color="primary" density="comfortable" @click="open = false" />
			</v-card-title>
			<v-card-text class="competitor-positioning-dialog__meta text-caption text-medium-emphasis">
				{{ context?.deliveryLocationLabel }}
				<template v-if="statementDateLabel">· {{ t('StatementOfDate') }} {{ statementDateLabel }}</template>
			</v-card-text>

			<v-card-text class="competitor-positioning-dialog__content" :class="{ 'competitor-positioning-dialog__content--mobile': isMobile }">
				<v-progress-linear v-if="loading" indeterminate color="primary" />
				<div v-else-if="!positioning.categories.length" class="text-center pa-8 text-disabled">{{ t('NoData') }}</div>
				<template v-else>
					<div class="competitor-positioning-dialog__summary">
						<v-icon v-if="!isMobile" class="competitor-positioning-dialog__summary-icon">workspace_premium</v-icon>
						<div class="competitor-positioning-dialog__summary-text">
							<span class="competitor-positioning-dialog__summary-brand">
								{{ positioning.summary.tag }}
								<strong v-if="positioning.summary.name">{{ positioning.summary.name }}</strong>
							</span>
							<span v-for="item in positioning.summary.items" :key="item.key" class="competitor-positioning-dialog__summary-item">
								{{ item.label }} :
								<strong>{{ item.strong }}</strong>
								{{ item.rest }}
							</span>
						</div>
					</div>

					<section v-for="category in positioning.categories" :key="category.id" class="competitor-positioning-dialog__category">
						<CompetitorPositioningCategoryBanner class="competitor-positioning-dialog__banner" :label="category.label" :industrial-count="category.industrialCount" :collapsible="isMobile" :expanded="!isMobile || expandedCategories.has(category.id)" @toggle="expandedCategories = toggled(expandedCategories, category.id)" />
						<AgGrid v-if="!isMobile" class="positioning-table" :row-data="rowsWithTotal(category)" :get-row-id="getRowId" :col-defs="tableColumnDefs" :get-row-class="getRowClass" row-group-panel-show="never" :side-bar="false" :auto-height="true" />
						<div v-else-if="expandedCategories.has(category.id)" class="competitor-positioning-dialog__cards">
							<CompetitorPositioningCard v-for="line in category.lines" :key="line.key" :line="line" :expanded="expandedCards.has(cardKey(category, line))" @toggle="expandedCards = toggled(expandedCards, cardKey(category, line))" />
						</div>
					</section>

					<div v-if="!isMobile" class="competitor-positioning-dialog__legend">
						<span class="competitor-positioning-dialog__legend-top">
							<span class="competitor-positioning-dialog__legend-swatch"></span>
							{{ t('PositioningLegendTop') }}
						</span>
						<span>{{ t('PositioningLegend') }}</span>
					</div>
				</template>
			</v-card-text>

			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn text @click="open = false" data-close>{{ t('Close') }}</v-btn>
			</v-card-actions>
		</v-card>
	</DialogControl>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useTranslator } from '@/composables/useTranslator';
import { tOverridable } from '@/i18n';
import { deviceUtils } from '@/utils/core/deviceUtils';
import { formatDate, EMPTY_DISPLAY } from '@/utils/core/format';
import { log } from '@/utils/core/vueLogger.js';
import { getCompetitorPositioning } from '@/services/competitorPositioningService';
import { presentPositioning, POSITIONING_COLUMNS } from './positioningDisplay';
import CompetitorPositioningCategoryBanner from './CompetitorPositioningCategoryBanner.vue';
import CompetitorPositioningCard from './CompetitorPositioningCard.vue';
import AgGrid from '@/components/agGrid/AgGrid.vue';
import IconValueRenderer from '@/components/agGrid/IconValueRenderer.vue';
import PositioningBrandCellRenderer from './PositioningBrandCellRenderer.vue';
import DialogControl from '@/components/controls/DialogControl.vue';

const t = useTranslator();

const props = defineProps({
	modelValue: { type: Boolean, default: false },
	overlayTarget: { type: [Object, null], default: null },
	context: { type: Object, default: null },
});
const emit = defineEmits(['update:modelValue']);

const open = computed({
	get: () => props.modelValue,
	set: (value) => emit('update:modelValue', value),
});

const result = ref(null);
const loading = ref(false);
const expandedCategories = ref(new Set());
const expandedCards = ref(new Set());

const isMobile = ref(deviceUtils.isLegacyMobile());
const onResize = () => {
	isMobile.value = deviceUtils.isLegacyMobile();
};

onMounted(() => window.addEventListener('resize', onResize));
onBeforeUnmount(() => window.removeEventListener('resize', onResize));

const positioning = computed(() => presentPositioning(result.value, t, tOverridable));
const statementDateLabel = computed(() => formatDate(props.context?.statementDate));

function cardKey(category, line) {
	return `${category.id}:${line.key}`;
}

function toggled(set, key) {
	const next = new Set(set);
	if (next.has(key)) next.delete(key);
	else next.add(key);
	return next;
}

const TABLE_DENSE = Object.freeze({ sortable: false, filter: false, resizable: false, enableValue: false, suppressHeaderMenuButton: true });

function totalRowFor(category) {
	return { key: 'total', kind: 'total', name: t('CategoryTotal'), tags: [], caption: '', showBalance: false, isTopPdl: false, display: category.totalDisplay };
}

function rowsWithTotal(category) {
	return [...category.lines, totalRowFor(category)];
}

function valueCellClass(params, key) {
	return ['ag-right-aligned-cell', key === 'pdl' && params.data?.isTopPdl ? 'positioning-table__cell--top' : null, params.data?.display?.[key] === EMPTY_DISPLAY ? 'positioning-table__cell--empty' : null].filter(Boolean);
}

const tableColumnDefs = computed(() => [
	{ headerName: t('Industrial'), colId: 'brand', minWidth: 220, flex: 1, ...TABLE_DENSE, cellRenderer: PositioningBrandCellRenderer },
	...POSITIONING_COLUMNS.map((column) => ({
		headerName: t(column.labelKey),
		colId: column.key,
		width: column.key === 'linear' || column.key === 'presentTheoretical' ? 110 : 90,
		...TABLE_DENSE,
		headerClass: 'ag-right-aligned-header',
		valueGetter: (p) => p.data?.display?.[column.key],
		cellClass: (p) => valueCellClass(p, column.key),
		cellRenderer: column.key === 'presentTheoretical' ? IconValueRenderer : undefined,
		cellRendererParams: column.key === 'presentTheoretical' ? (p) => ({ icon: p.data?.showBalance ? 'balance' : null }) : undefined,
	})),
]);

function getRowId(params) {
	return params.data?.key ?? '';
}

function getRowClass(params) {
	return params.data?.kind ? `positioning-table__row--${params.data.kind}` : undefined;
}

watch(open, (isOpen) => {
	if (!isOpen) return;
	expandedCategories.value = new Set();
	expandedCards.value = new Set();
	load();
});

let loadRequestId = 0;

async function load() {
	const requestId = ++loadRequestId;
	loading.value = true;
	try {
		const data = await getCompetitorPositioning(props.context?.statements);
		if (requestId === loadRequestId) {
			result.value = data;
			expandedCategories.value = new Set((data?.categories || []).map((category) => category.id));
		}
	} catch (error) {
		log('error', 'CompetitorPositioning', `Positioning summary failed: ${error.message}`);
		if (requestId === loadRequestId) result.value = null;
	} finally {
		if (requestId === loadRequestId) loading.value = false;
	}
}
</script>

<style scoped lang="scss">
@use '@/styles/variables' as *;

.competitor-positioning-dialog {
	&__meta.v-card-text {
		flex: none;
		height: auto;
		padding-top: 0;
	}

	&__content {
		gap: 0 !important;
		background: rgb(var(--v-theme-surface));

		&--mobile {
			background: rgba(var(--v-theme-on-surface), 0.03);
		}
	}

	&__summary {
		display: flex;
		align-items: center;
		gap: 12px;
		margin: 20px 24px;
		padding: 14px 18px;
		border-radius: 12px;
		border: thin solid rgba(var(--v-theme-info), 0.25);
		background: rgba(var(--v-theme-info), 0.07);
		box-shadow: inset 4px 0 0 rgb(var(--v-theme-info));
	}

	&__summary-icon {
		flex: none;
		color: rgb(var(--v-theme-info));
	}

	&__summary-text {
		display: flex;
		flex-wrap: wrap;
		align-items: baseline;
		gap: 4px 0;
		min-width: 0;
		font-size: $font-size;
		color: $color-primary;
	}

	&__summary-item::before {
		content: '·';
		margin: 0 10px;
		color: rgba(var(--v-theme-on-surface), 0.4);
	}

	&__category + &__category {
		margin-top: 24px;
	}

	&__legend {
		display: flex;
		flex-direction: column;
		gap: 6px;
		padding: 16px 24px 24px;
		font-size: $font-size-small;
		color: rgba(var(--v-theme-on-surface), 0.6);
	}

	&__legend-top {
		display: inline-flex;
		align-items: center;
		gap: 8px;
	}

	&__legend-swatch {
		flex: none;
		width: 18px;
		height: 18px;
		border-radius: 4px;
		background: rgba(var(--v-theme-success), 0.14);
		border: thin solid rgba(var(--v-theme-success), 0.4);
	}

	&__content--mobile {
		.competitor-positioning-dialog__summary {
			margin: 16px 12px;
		}

		.competitor-positioning-dialog__summary-brand {
			flex-basis: 100%;
		}

		.competitor-positioning-dialog__summary-brand + .competitor-positioning-dialog__summary-item::before {
			display: none;
		}

		.competitor-positioning-dialog__category + .competitor-positioning-dialog__category {
			margin-top: 0;
		}

		.competitor-positioning-dialog__banner {
			position: sticky;
			top: 0;
			z-index: 1;
		}

		.competitor-positioning-dialog__cards {
			display: flex;
			flex-direction: column;
			gap: 10px;
			padding: 12px;
		}
	}

	.positioning-table {
		:deep(.positioning-table__cell--top) {
			background: rgba(var(--v-theme-success), 0.14) !important;
			color: rgb(var(--v-theme-success));
			font-weight: 700;
		}

		:deep(.positioning-table__cell--empty) {
			color: rgba(var(--v-theme-on-surface), 0.4);
		}

		:deep(.ag-row.positioning-table__row--myBrand) {
			background: rgba(var(--v-theme-primary), 0.08) !important;
			box-shadow: inset 4px 0 0 rgb(var(--v-theme-primary));
		}

		:deep(.ag-row.positioning-table__row--subset) {
			background: rgba(var(--v-theme-primary), 0.04) !important;
		}

		:deep(.ag-row.positioning-table__row--total) {
			background: rgba(var(--v-theme-on-surface), 0.04) !important;
			color: $color-primary;
			font-weight: 700;
		}
	}
}
</style>
